import { useEffect, useState } from "react";
import { KeyRound, Plus, Trash2, Copy, Check, Lock, Terminal } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { tr, locale } from "@/lib/i18n";
const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/v1/engine/calculate`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString(locale(), { dateStyle: "short", timeStyle: "short" }) : "—");

const Card = ({ title, icon: Icon, children, testid, delay = 0 }) => (
  <section className="bg-white border border-slate-200 rounded-lg sr-rise" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
    <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2.5">
      <Icon className="w-4 h-4 text-[#0F52BA]" /><h2 className="font-display font-semibold text-slate-900">{title}</h2>
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const PasswordForm = () => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/password", f); toast.success(tr("Password aggiornata")); setF({ current_password: "", new_password: "" }); }
    catch (ex) { toast.error(apiError(ex)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
      <div className="space-y-1.5"><Label className="text-xs text-slate-500">{tr("Password attuale")}</Label>
        <Input type="password" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="pw-current-input" /></div>
      <div className="space-y-1.5"><Label className="text-xs text-slate-500">{tr("Nuova password (min. 8)")}</Label>
        <Input type="password" required minLength={8} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="pw-new-input" /></div>
      <Button type="submit" disabled={busy} variant="outline" data-testid="pw-submit-btn">{tr("Aggiorna")}</Button>
    </form>
  );
};

const NewKey = ({ k, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(k.key); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="new-key-box">
      <div className="text-sm font-medium text-emerald-900">{tr("API key \"")}{k.name}{tr("\" creata. Copiala ora: non verrà più mostrata.")}</div>
      <div className="flex gap-2">
        <code className="flex-1 font-tech text-xs bg-white border border-emerald-200 rounded px-3 py-2 break-all" data-testid="new-key-value">{k.key}</code>
        <Button size="icon" variant="outline" onClick={copy} data-testid="new-key-copy-btn">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</Button>
      </div>
      <button type="button" className="text-xs text-emerald-800 hover:underline" onClick={onClose} data-testid="new-key-dismiss">{tr("Ho salvato la chiave")}</button>
    </div>
  );
};

const ApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);
  const load = () => api.get("/keys").then(({ data }) => setKeys(data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/keys", { name }); setFresh(data); setName(""); load(); } catch (ex) { toast.error(apiError(ex)); }
  };
  const revoke = async (id) => { await api.delete(`/keys/${id}`); toast.success(tr("API key revocata")); load(); };
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">{tr("Le API key permettono a un software esterno di usare il")} <b>{tr("motore di calcolo strutturale")}</b> {tr("(nessun accesso ai progetti). Ogni chiamata è conteggiata.")}</p>
      <form onSubmit={create} className="flex gap-2">
        <Input required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder={tr("Nome (es. Gestionale offerte)")} data-testid="key-name-input" />
        <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0D47A1]" data-testid="key-create-btn"><Plus className="w-4 h-4 mr-1.5" />{tr("Genera")}</Button>
      </form>
      {fresh && <NewKey k={fresh} onClose={() => setFresh(null)} />}
      {keys.length === 0 ? <div className="text-sm text-slate-400" data-testid="keys-empty">{tr("Nessuna API key attiva.")}</div> : (
        <table className="w-full text-sm" data-testid="keys-table">
          <thead><tr className="text-left text-xs font-tech uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="py-2">{tr("Nome")}</th><th className="py-2">{tr("Chiave")}</th><th className="py-2">{tr("Creata")}</th><th className="py-2">{tr("Ultimo uso")}</th><th className="py-2">{tr("Chiamate")}</th><th />
          </tr></thead>
          <tbody>{keys.map((k) => (
            <tr key={k.id} className="border-b border-slate-100" data-testid={`key-row-${k.id}`}>
              <td className="py-2.5 font-medium">{k.name}</td><td className="py-2.5 font-tech text-xs text-slate-500">{k.prefix}…</td>
              <td className="py-2.5 text-slate-500">{fmtDate(k.created_at)}</td><td className="py-2.5 text-slate-500">{fmtDate(k.last_used_at)}</td>
              <td className="py-2.5 font-tech" data-testid={`key-uses-${k.id}`}>{k.uses}</td>
              <td className="py-2.5 text-right"><Button variant="ghost" size="icon" title={tr("Revoca")} onClick={() => revoke(k.id)} className="hover:text-amber-700" data-testid={`key-revoke-${k.id}`}><Trash2 className="w-4 h-4" /></Button></td>
            </tr>))}</tbody>
        </table>
      )}
    </div>
  );
};

const Docs = () => (
  <div className="space-y-3 text-sm text-slate-600">
    <p><span className="font-tech text-xs px-2 py-0.5 rounded bg-blue-50 text-[#0F52BA]">POST</span> <code className="font-tech text-xs break-all" data-testid="api-endpoint">{ENDPOINT}</code></p>
    <p>{tr("Intestazione")} <code className="font-tech text-xs">X-API-Key</code>{tr(", corpo JSON con i dati del progetto (stessa struttura dei progetti Synvora Design:")} <code className="font-tech text-xs">roof_type, altitude, building_height, engine, roofs, modules</code>{tr("). La risposta contiene stato, verifiche, utilizzazioni, distinta e avvisi.")}</p>
    <pre className="bg-slate-900 text-slate-100 rounded-md p-4 text-xs overflow-x-auto font-tech">{`curl -X POST "${ENDPOINT}" \\
  -H "X-API-Key: srk_..." \\
  -H "Content-Type: application/json" \\
  -d @progetto.json`}</pre>
  </div>
);

export default function Account() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-10 space-y-6">
        <div className="sr-rise">
          <div className="text-xs font-tech uppercase tracking-wider text-[#0F52BA] mb-2">{tr("Account")}</div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight text-slate-900" data-testid="account-title">{user.name}</h1>
          <p className="text-slate-500 mt-2">{user.email} · {user.role === "admin" ? tr("Super-admin") : user.group_role === "admin" ? tr("Admin azienda") : tr("Membro")} {tr("· Azienda:")} <span data-testid="account-group">{user.group_name || tr("nessuno")}</span></p>
        </div>
        <Card title={tr("API key per integrazioni")} icon={KeyRound} testid="account-keys-card" delay={60}><ApiKeys /></Card>
        <Card title={tr("Uso dell'API")} icon={Terminal} testid="account-docs-card" delay={120}><Docs /></Card>
        <Card title={tr("Cambia password")} icon={Lock} testid="account-password-card" delay={180}><PasswordForm /></Card>
      </main>
    </div>
  );
}
